import React, { useEffect, useMemo, useState } from 'react';
import {
  Field,
  Input,
  Button,
  Text,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { Info20Regular } from '@fluentui/react-icons';
import BaseDialog from '../BaseDialog';

/**
 * Manual PR services-withholding (Sec. 1062.03) amount adjustment.
 *
 * The withholding is normally a rate × the pre-tax subtotal. This dialog is the
 * escape hatch for a one-off flat holdback that a percentage of THIS invoice's
 * subtotal can't express — e.g. carrying forward the 10% a prior invoice failed
 * to deduct. Saving stores orders.pr_withholding_amount_cents (a flat cents
 * override that supersedes the rate); clearing reverts to the automatic
 * rate-based calculation.
 *
 * It is NOT a discount: recognized revenue (the Total) is unchanged — only the
 * customer's Balance due drops by the held-back amount.
 */
interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** Order total incl. taxes (cents) — drives the Balance due preview. */
  totalInCents: number;
  /** Current manual override in cents, or null when none is set (auto/rate). */
  currentAmountCents: number | null;
  /** The auto (rate-based) holdback in cents and its rate label, shown for
   *  reference so staff can match or deliberately depart from it. */
  autoAmountCents: number;
  autoRateLabel?: string;
  /** Persist the override. cents = flat holdback (0 zeroes it out); null clears
   *  back to the automatic calculation. Rejects on failure. */
  onSave: (cents: number | null) => Promise<void>;
}

const useStyles = makeStyles({
  body: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('16px'),
  },
  plannedNote: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
    ...shorthands.padding('10px', '12px'),
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.borderRadius('4px'),
    color: tokens.colorNeutralForeground2,
    fontSize: '13px',
  },
  autoRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.gap('8px'),
  },
  preview: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('6px'),
    ...shorthands.padding('12px'),
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.borderRadius('4px'),
  },
  previewRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  previewDivider: {
    height: '1px',
    backgroundColor: tokens.colorNeutralStroke2,
    ...shorthands.margin('2px', '0'),
  },
  error: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: '13px',
  },
  actionsLeft: {
    marginRight: 'auto',
  },
});

const fmtUsd = (cents: number) =>
  ((cents || 0) / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

export const WithholdingAdjustmentDialog: React.FC<Props> = ({
  isOpen,
  onClose,
  totalInCents,
  currentAmountCents,
  autoAmountCents,
  autoRateLabel,
  onSave,
}) => {
  const styles = useStyles();
  const [amountStr, setAmountStr] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    // Seed the field with the existing override (in dollars); blank when none.
    setAmountStr(currentAmountCents != null ? (currentAmountCents / 100).toFixed(2) : '');
    setBusy(false);
    setError(null);
  }, [isOpen, currentAmountCents]);

  // Resolve the entered dollars to whole cents. Blank → null (no override).
  const enteredCents = useMemo<number | null>(() => {
    if (amountStr.trim() === '') return null;
    const n = parseFloat(amountStr);
    if (!Number.isFinite(n) || n < 0) return 0;
    return Math.round(n * 100);
  }, [amountStr]);

  const holdbackCents = enteredCents ?? 0;
  const balanceDueCents = Math.max(0, totalInCents - holdbackCents);
  const hasOverride = currentAmountCents != null;

  const persist = async (cents: number | null) => {
    setBusy(true);
    setError(null);
    try {
      await onSave(cents);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save the adjustment.');
      setBusy(false);
    }
  };

  return (
    <BaseDialog
      isOpen={isOpen}
      onClose={onClose}
      title="Adjust PR withholding"
      maxWidth="460px"
      actions={
        <>
          {hasOverride && (
            <Button
              className={styles.actionsLeft}
              appearance="subtle"
              disabled={busy}
              onClick={() => persist(null)}
            >
              Clear override
            </Button>
          )}
          <Button appearance="secondary" disabled={busy} onClick={onClose}>
            Cancel
          </Button>
          <Button appearance="primary" disabled={busy} onClick={() => persist(enteredCents)}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </>
      }
    >
      <div className={styles.body}>
        <div className={styles.plannedNote}>
          <Info20Regular />
          <span>
            A flat withholding amount that overrides the automatic calculation for
            this order — for a one-off correction (e.g. the 10% a prior invoice
            didn&apos;t deduct). Clearing it reverts to the automatic rate.
          </span>
        </div>

        {autoAmountCents > 0 && (
          <div className={styles.autoRow}>
            <Text>
              Automatic{autoRateLabel ? ` (${autoRateLabel})` : ''}: {fmtUsd(autoAmountCents)}
            </Text>
            <Button
              size="small"
              appearance="secondary"
              disabled={busy}
              onClick={() => setAmountStr((autoAmountCents / 100).toFixed(2))}
            >
              Use this
            </Button>
          </div>
        )}

        <Field label="Withholding amount (USD)">
          <Input
            size="large"
            value={amountStr}
            onChange={(_, d) => {
              // Numbers only — strip non-digits/decimal and collapse to a single dot.
              let v = d.value.replace(/[^0-9.]/g, '');
              const firstDot = v.indexOf('.');
              if (firstDot !== -1) {
                v = v.slice(0, firstDot + 1) + v.slice(firstDot + 1).replace(/\./g, '');
              }
              setAmountStr(v);
            }}
            placeholder="0.00"
            inputMode="decimal"
            contentBefore={<Text>$</Text>}
            autoFocus
          />
        </Field>

        <div className={styles.preview}>
          <div className={styles.previewRow}>
            <Text>Total incl. taxes</Text>
            <Text>{fmtUsd(totalInCents)}</Text>
          </div>
          <div className={styles.previewRow}>
            <Text>PR withholding</Text>
            <Text>{holdbackCents > 0 ? `-${fmtUsd(holdbackCents)}` : fmtUsd(0)}</Text>
          </div>
          <div className={styles.previewDivider} />
          <div className={styles.previewRow}>
            <Text weight="semibold">Balance due</Text>
            <Text weight="semibold">{fmtUsd(balanceDueCents)}</Text>
          </div>
        </div>

        {error && <Text className={styles.error}>{error}</Text>}
      </div>
    </BaseDialog>
  );
};

export default WithholdingAdjustmentDialog;
